import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';

import { ScrollLink } from '@components/Typography/TextLink/ScrollLink';
import { type ReactElement, useEffect, useState } from 'react';

/*
 * This component creates an alphabetical list of clickable buttons based on an array of items. Letters
 * in the list are generated based on the item displayAttribute values present.
 * Links to the products are generated passing in the idAttribute and the alphabet list is generated using
 * the displayAttribute variable.
 *
 * @param data: Array<Object>
 * @param displayAttribute: string
 * @param idAttribute: string
 * @returns <ReactElement>
 */
export function AlphabeticScroll({
  data,
  displayAttribute,
  idAttribute,
}: {
  data: Array<Object>;
  displayAttribute: keyof BaseProduct;
  idAttribute: string;
}): ReactElement {
  // displayLetters state contains the displayed letters in the list
  const [displayLetters, setDisplayLetters] = useState<Array<string>>([]);
  // lettersMap contains the mapping between displayed letters and the corresponding item ID
  const [lettersMap, setLettersMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    /* 
    Generate the alphabetic list to click through, creates letters only
    if they are present in the passed in data.
    */
    const lettersMapToIds = new Map();
    var Regx = /^[A-Za-z]$/;

    data.forEach((row, i) => {
      let firstCharacter = row[displayAttribute][0].toUpperCase();

      if (!Regx.test(firstCharacter)) {
        firstCharacter = '#';
      } else {
        firstCharacter = firstCharacter.toUpperCase();
      }

      if (lettersMapToIds.get(firstCharacter) == null) {
        lettersMapToIds.set(firstCharacter, row[idAttribute]);
      }
    });
    setLettersMap(lettersMapToIds);
    setDisplayLetters(Array.from(lettersMapToIds.keys()));
  }, [data, displayAttribute, idAttribute]);

  return (
    <div className="sticky top-10 grid h-auto grid-flow-row place-items-center gap-0.5 p-0.5 sm:h-[80vh]">
      <h5>Find</h5>
      {displayLetters?.map((row, i) => (
        // Use ScrollLink here because we don't want it to look like an actual Button
        <ScrollLink
          key={`alphabet-nav-${i}`}
          id={`${lettersMap.get(row as string)}`}
          className="hover:text-primary min-w-4 p-[0.2rem] text-grey-dark"
        >
          {row}
        </ScrollLink>
      ))}
    </div>
  );
}
